import { Color } from '../entities/Color';
import { ComponentKewlox } from './ComponentKewlox';
import {
  PropsComponent,
  PropsGeneral,
  SearchInfo,
  UpperShelfEntity,
} from '../Data';
import { DataStorage, getColors, getPiecesProps } from '../utils/dataStorage';
import {
  NATURAL_COLOR,
  PaneMaterial,
  PlateType,
  STANDARD_COLOR,
} from '../constants';
import { CustomPiece } from './CustomPiece';
import {
  computePiecePrice,
  computeWeightWithDensity,
  getPropsKey,
} from '../utils/helpers';
import { getMaterialColor } from '../utils/colorHelper';

export class UpperShelf extends ComponentKewlox {
  readonly width: number;
  readonly depth: number;

  readonly material: PaneMaterial;
  readonly color: Color;

  static _lookup = ['width', 'depth'];

  static _gapWidth = 0;
  static _gapHeight = 0;
  static _gapDepth = 0;

  constructor(
    id: number,
    name: string,
    reference: string,
    price: number,
    weigth: number,
    forSale: boolean,
    visible: boolean,
    height: number,
    width: number,
    depth: number,
    color: Color,
    material?: PaneMaterial,
  ) {
    super(id, name, reference, price, weigth, forSale, visible, height);
    this.width = width;
    this.depth = depth;
    this.material = material ?? getMaterialColor(color);
    this.color = color;
  }

  get typeComponent() {
    return 'UpperShelf';
  }

  static fromJSON(json: UpperShelfEntity): UpperShelf {
    let objColor: Color;
    if (typeof json.color === 'number') {
      const colors = getColors();
      objColor = colors[0];
      for (const colorItem of colors) {
        if (colorItem.id === json.color) {
          objColor = colorItem;
        }
      }
    } else {
      objColor = json.color as Color;
    }

    const o = Object.create(UpperShelf.prototype);
    return Object.assign(o, this, json, {
      color: objColor,
      material: getMaterialColor(objColor),
    });
  }

  static reviver(key: string, value: any): any {
    return key === '' ? UpperShelf.fromJSON(value) : value;
  }

  compareTo(otherComponent: ComponentKewlox): number {
    return (
      this.width - (otherComponent as UpperShelf).width ||
      this.depth - (otherComponent as UpperShelf).depth
    );
  }

  includeInto(otherComponent: ComponentKewlox): boolean {
    return (
      (otherComponent as UpperShelf).width - this.width >= 0 &&
      (otherComponent as UpperShelf).depth - this.depth >= 0
    );
  }

  static getSearchInfos(
    width: number,
    depth: number,
    color: Color,
  ): SearchInfo {
    const height = 18;
    return {
      lookup: this._lookup,
      key: { height, width, depth, color: color.id },
    };
  }

  getKey(): string {
    return UpperShelf.generateKey(this.width, this.depth, this.color);
  }

  static generateKey(width: number, depth: number, color: Color) {
    const searchInfos = UpperShelf.getSearchInfos(width, depth, color);
    return JSON.stringify(searchInfos.key);
  }

  static standardUpperShelf(
    width: number,
    depth: number,
    position: number,
  ): CustomPiece<UpperShelf> {
    /**
     * Return a standard Door as custom piece
     */
    const colors = DataStorage.colors;
    const color = colors.find(item => item.reference === STANDARD_COLOR);
    const pieces = DataStorage.pieces;
    const searchInfo = UpperShelf.getSearchInfos(width, depth, color as Color);
    return pieces.upperShelves.getCustom(searchInfo, position);
  }

  get realHeight() {
    return UpperShelf.computeRealHeight(this.height);
  }

  get realWidth() {
    return UpperShelf.computeRealWidth(this.width);
  }

  get realDepth() {
    return UpperShelf.computeRealDepth(this.depth);
  }

  static computeRealWidth(
    width: number,
    glass = false,
    largeDoor = false,
  ): number {
    // Get the real width dimension of the upper shelf

    // This values were measured manually by Kewlox
    return width + UpperShelf._gapWidth;
  }

  static computeRealHeight(
    height: number,
    plateType?: PlateType | undefined,
    glass = false,
  ): number {
    // Get the real (physically) height dimension of the upper shelf

    // This values were mesured manually by Kewlox
    return height + UpperShelf._gapHeight;
  }

  static computeRealDepth(depth: number): number {
    // Get the real (physically) depth dimension of the upper shelf

    // This values were mesured manually by Kewlox
    return depth + UpperShelf._gapDepth;
  }

  static computeBasePrice(
    width: number,
    depth: number,
    isNatural: boolean,
    props: PropsComponent,
    propsGen: PropsGeneral,
  ) {
    /**
     * This formula came from the PRO100 Excel linked file
     *
     * Formule : ((surface*K1)+K2)*K3
     *
     * surface in µ
     *
     */
    width = width > props.minDim ? width : props.minDim;
    depth = depth > props.minDim ? depth : props.minDim;

    const widthMicr = width / 1000;
    const depthMicr = depth / 1000;

    const surface = widthMicr * depthMicr;
    const perimeter = (widthMicr + depthMicr) * 2;

    const upperShelfWorkforce = isNatural
      ? propsGen.upperShelfNaturalWorkforce
      : propsGen.upperShelfWorkforce;

    const k1 = props.k1 ?? 0;
    const k2 = props.k2 ?? 0;
    const k3 = props.k3 ?? 0;
    const k5 = props.k5 ?? 0;

    const tap = perimeter * k5;
    let price =
      (surface * k1 + k2 + tap + upperShelfWorkforce) * k3 +
      propsGen.upperShelfADF;

    price *= propsGen.coef;
    price = Math.round(price * 100) / 100;

    return price;
  }

  public computePrice(customPiece: CustomPiece<ComponentKewlox>): number {
    const props: PropsComponent | void = UpperShelf.getProperties(
      this.material,
      this.color,
    );
    const propsGen: PropsGeneral = getPiecesProps().general;

    if (!props) return 0;

    const priceForDimensions = UpperShelf.computeBasePrice(
      customPiece.realWidth,
      customPiece.realDepth,
      this.color.reference === NATURAL_COLOR,
      props,
      propsGen,
    );

    const priceForBase = UpperShelf.computeBasePrice(
      this.realWidth,
      this.realDepth,
      this.color.reference === NATURAL_COLOR,
      props,
      propsGen,
    );

    return computePiecePrice(
      priceForDimensions,
      priceForBase,
      customPiece.custom,
      customPiece.calibrated,
      propsGen,
    );
  }

  public computeWeight(customPiece: CustomPiece<ComponentKewlox>): number {
    const props: PropsComponent | void = UpperShelf.getProperties(
      this.material,
      this.color,
    );

    if (!props) return 0;

    return computeWeightWithDensity(
      customPiece.realWidth,
      customPiece.realHeight,
      customPiece.realDepth,
      props.density,
    );
  }

  static getProperties(
    material: PaneMaterial,
    color: Color,
  ): PropsComponent | void {
    const key = getPropsKey(material, color);

    if (getPiecesProps().tablets.hasOwnProperty(key)) {
      return getPiecesProps().tablets[key];
    }
  }
}
